import { ChartTypeUsage } from 'src/app/types/plan';

export interface ChartUsageDisplay {
  /** Published charts of that type. */
  usage: number;
  limit: number | null;
  unlimited: boolean;
  /** A bar needs a limit to measure against: none for unlimited, none for a limit of 0. */
  showBar: boolean;
  /** Bar width, capped at 100. */
  percent: number;
  reached: boolean;
  /** Above the limit, not just at it: after a downgrade, an admin change, or an undo of an unpublish. */
  over: boolean;
  /** Bar colour, on the same thresholds as the AI and stock notification bars. */
  state: 'normal' | 'warning' | 'error';
}

/**
 * How one chart type's published usage is shown, in one place so the Plan & usage page, the sidebar
 * and the admin view can't disagree. Undefined when the backend sent no chart usage (older than
 * GOO-396): the row isn't rendered at all.
 */
export const chartUsageDisplay = (usage: ChartTypeUsage | undefined): ChartUsageDisplay | undefined => {
  if (!usage) return undefined;

  const { usage: used, limit } = usage;

  if (limit === null)
    return {
      usage: used,
      limit,
      unlimited: true,
      showBar: false,
      percent: 0,
      reached: false,
      over: false,
      state: 'normal',
    };

  const percent = limit === 0 ? 100 : Math.min(100, (used / limit) * 100);

  return {
    usage: used,
    limit,
    unlimited: false,
    showBar: limit > 0,
    percent,
    reached: used >= limit,
    over: used > limit,
    state: percent < 50 ? 'normal' : percent < 80 ? 'warning' : 'error',
  };
};
